import { Row, Col, Typography, Table, Button, message } from "antd";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { PartnerVariant, usePartnerServicesConfig } from "hooks/usePartnerServicesConfig";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const MESSAGE_KEY = "SAVE_GIG_PAYOUTS";

const SaveGigPayoutsPage = ({ variant = "ppd" }: { variant?: PartnerVariant }) => {
  useDocumentTitle("Save Gig Payouts");
  const config = usePartnerServicesConfig(variant);
  const {
    dispatch,
    processingGigPayouts,
    status,
    error,
    partnerRoutes,
    saveGigPayoutsThunk,
    cancelGigPayoutsThunk,
    removeGigPayoutsAction,
  } = config;

  const navigate = useNavigate();

  useEffect(() => {
    const { timeUploaded } = processingGigPayouts;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
      if (minsPassed >= 15) dispatch(removeGigPayoutsAction());
    }
    if (!Object.keys(processingGigPayouts).length) {
      navigate(`/${partnerRoutes.UPLOAD_GIG_PAYOUTS}`);
    }
  }, [processingGigPayouts, navigate, dispatch, removeGigPayoutsAction, partnerRoutes]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Saving Gig Payouts...", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({ content: "Gig Payouts saved successfully!", duration: 5, key: MESSAGE_KEY });
      navigate(`/${partnerRoutes.UPLOAD_GIG_PAYOUTS}`);
    } else if (status === "cancelling") {
      message.loading({ content: "Cancelling Gig Payouts...", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }
    return () => { message.destroy(); };
  }, [status, error, navigate, partnerRoutes]);

  const { Text } = Typography;
  const { headerMapping } = processingGigPayouts;
  const headerMappingWithExtras = headerMapping
    ? headerMapping + ",actualEarnings:Actual Earnings,differenceAmount:Difference Amount"
    : undefined;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithExtras),
    [headerMappingWithExtras]
  );

  let content;
  if (!Object.keys(processingGigPayouts).length) {
    content = <Text>Nothing to show here!</Text>;
  } else {
    const headers = generateMemoizedHeaders || {};
    const data = processingGigPayouts.successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
    const columns = Object.keys(headers).map((key) => ({
      key,
      title: headers[key],
      dataIndex: key,
    }));
    content = (
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        rowKey="key"
        scroll={{ y: 500, x: "100vh" }}
      />
    );
  }

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Save Gig Payouts" />
        </Col>
      </Row>
      <Row>
        <Col>{content}</Col>
      </Row>
      {!!Object.keys(processingGigPayouts).length && processingGigPayouts.key && (
        <Row style={{ marginTop: "20px", gap: "10px" }}>
          <Col>
            <Button
              type="primary"
              onClick={() => dispatch(saveGigPayoutsThunk())}
              loading={status === "loading"}
            >
              Save
            </Button>
          </Col>
          <Col>
            <Button
              onClick={() => dispatch(cancelGigPayoutsThunk())}
              loading={status === "cancelling"}
              danger
            >
              Cancel
            </Button>
          </Col>
        </Row>
      )}
    </>
  );
};

export default SaveGigPayoutsPage;
